import { useState, useEffect } from "react";
import { PDFDocument } from "pdf-lib";
import CommanSpinner from "./CommanSpinner";
import { useHistory } from "react-router-dom";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { isDemo } from "../App";

// Regex for validation (matching original)
const CommaAndHypenAndIntegerReg = /^[\d,\s-]+$/;

const PDFSplitter = ({
  uploadedPdf, // Original PDF file
  totalPages, // Total pages in PDF
  existingChildPages = [], // Already split pages
  onSplitComplete, // Callback on successful split
  onSplitError, // Callback on error
  uploadEndpoint, // API endpoint for upload
  documentId, // Document ID
  onClose, // Modal close callback
  disabled = false, // Disable split button
  className = "", // Additional CSS classes
  showSuccessRedirect = true, // Whether to show redirect button on success
}) => {
  // States matching original component
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pageNumber, setPageNumber] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [allPages, setAllPages] = useState([]);
  const [newPdf, setNewPdf] = useState(null);
  const [childCreatedList, setChildCreatedList] = useState(0);
  const [creatdChildPdfList, setCreatdChildPdfList] = useState([]);
  const history = useHistory();

  const handleSplitClick = () => {
    setShowSplitModal(true);
    resetState();
  };

  const resetState = () => {
    setPageNumber("");
    setErrorMsg("");
    setAllPages([]);
    setNewPdf(null);
    setChildCreatedList(0);
    setCreatdChildPdfList([]);
  };

  const closeModal = () => {
    setShowSplitModal(false);
    setShowSuccessModal(false);
    resetState();
    onClose?.();
  };

  const goToInbox = () => {
    closeModal();

    // Store the document ID in sessionStorage before navigating
    sessionStorage.setItem("recentlySplitDocId", documentId);

    // Call the onSplitComplete callback
    onSplitComplete?.();

    // Navigate to inbox with a query parameter indicating split occurred
    history.push({
      pathname: "/home",
      state: { fromSplit: true, splitDocumentId: documentId },
    });
  };

  // Split and range processing (matching original logic)
  const splitAndRange = (text, addToChild, sumPages) => {
    if (text?.includes("-")) {
      let dashSplit = text?.split("-");
      if (dashSplit?.length > 1) {
        if (parseInt(dashSplit[1]) > parseInt(dashSplit[0])) {
          let rangeValue = Array.from(
            Array(parseInt(dashSplit[1])),
            (_, index) => index + 1,
          );
          rangeValue = rangeValue?.slice(parseInt(dashSplit[0]) - 1);
          addToChild.push({ childPdf: [...rangeValue] });
          sumPages = [...sumPages, ...rangeValue];
          return { addToChild, sumPages };
        } else {
          setErrorMsg(`Please Enter Range Valid Start Value. :${dashSplit[0]}`);
          return "error greater";
        }
      } else {
        setErrorMsg("Please Enter Natural Format Value. eg: 1,2,3-6");
        return "error Range";
      }
    } else {
      addToChild.push({ childPdf: [parseInt(text)] });
      sumPages = [...sumPages, parseInt(text)];
      return { addToChild, sumPages };
    }
  };

  // Page validation (matching original)
  const pageValidation = (createdChildPdfListTemp, totalPagesArr) => {
    let largePageNO = totalPagesArr?.some((element) => element > totalPages);
    let isZero = totalPagesArr?.some((element) => element === 0);
    let repeated = totalPagesArr?.some(
      (element, index) => totalPagesArr.indexOf(element) !== index,
    );
    let childExist = existingChildPages?.some((element) =>
      totalPagesArr?.includes(element),
    );

    if (repeated) {
      setErrorMsg("Do not repeat page number.");
    } else if (childExist) {
      setErrorMsg("Page exist in child PDF.");
    } else if (isZero) {
      setErrorMsg("Please Enter Valid Value.");
    } else if (largePageNO) {
      setErrorMsg("Page number must be smaller than last page.");
    } else {
      createPDF(createdChildPdfListTemp[childCreatedList]?.childPdf);
    }
  };

  const handleSplit = () => {
    let createdChildPdfListTemp = [];
    let totalPages = [];
    let isError = "";

    if (!pageNumber) {
      setErrorMsg("Please Enter Value.");
      return;
    }

    if (!CommaAndHypenAndIntegerReg.test(pageNumber)) {
      setErrorMsg("Please Enter Natural Format Value. eg: 1,2,3-6");
      return;
    }

    if (pageNumber?.includes(",")) {
      let PagesArray = pageNumber?.split(",").filter((el) => el);

      PagesArray?.forEach((element) => {
        let returnValue = splitAndRange(
          element,
          createdChildPdfListTemp,
          totalPages,
        );
        if (typeof returnValue === "string") {
          isError = returnValue;
        } else {
          createdChildPdfListTemp = returnValue.addToChild;
          totalPages = returnValue.sumPages;
        }
      });
    } else {
      let returnValue = splitAndRange(
        pageNumber,
        createdChildPdfListTemp,
        totalPages,
      );
      if (typeof returnValue === "string") {
        isError = returnValue;
      } else {
        createdChildPdfListTemp = returnValue.addToChild;
        totalPages = returnValue.sumPages;
      }
    }

    if (!isError) {
      pageValidation(createdChildPdfListTemp, totalPages);
      setCreatdChildPdfList(createdChildPdfListTemp);
    }
  };

  const createPDF = async (pages) => {
    setIsLoading(true);
    try {
      // Get the PDF file as an ArrayBuffer
      const existingPdfBytes = await uploadedPdf.arrayBuffer();

      // Load the existing PDF
      const loadedPdf = await PDFDocument.load(existingPdfBytes);

      // Create a new PDF document
      const newPdfDoc = await PDFDocument.create();

      // Copy specified pages
      for (let i = 0; i < pages.length; i++) {
        const [copiedPage] = await newPdfDoc.copyPages(loadedPdf, [
          pages[i] - 1,
        ]);
        newPdfDoc.addPage(copiedPage);
      }

      // Save the new PDF
      const newPdfBytes = await newPdfDoc.save();

      // Create a new File object
      const file = new File([newPdfBytes], uploadedPdf.name, {
        type: "application/pdf",
      });

      if (isDemo) {
        // In demo mode, simulate API request with a delay
        sessionStorage.setItem("showSplit", true);
        console.log("Demo mode: Simulating API request");

        // Simulate a 2-second delay
        await new Promise(resolve => setTimeout(resolve, 3500));

        // Proceed as if API call was successful
        setShowSuccessModal(true);
        setShowSplitModal(false);

        if (childCreatedList + 1 === creatdChildPdfList.length) {
          onSplitComplete?.();
        } else {
          setChildCreatedList((prev) => prev + 1);
        }
      } else {
        // Real mode - make the actual API request
        const formData = new FormData();
        formData.append("id", documentId);
        formData.append("pages", pages.join(","));
        formData.append("pdfFile", file);

        const response = await fetch(uploadEndpoint, {
          method: "POST",
          body: formData,
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setShowSuccessModal(true);
        setShowSplitModal(false);

        if (childCreatedList + 1 === creatdChildPdfList.length) {
          onSplitComplete?.();
        } else {
          setChildCreatedList((prev) => prev + 1);
        }
      }
    } catch (error) {
      console.error("PDF creation error:", error);
      setErrorMsg(error.message || "Error creating PDF. Please try again.");
      onSplitError?.(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (childCreatedList > 0 && childCreatedList < creatdChildPdfList.length) {
      createPDF(creatdChildPdfList[childCreatedList].childPdf);
    }
  }, [childCreatedList]);

  useEffect(() => {
    let navigationTimer;

    if (showSuccessModal) {
      // Set a timer to automatically navigate after 2.5 seconds
      navigationTimer = setTimeout(() => {
        goToInbox();
      }, 2000);
    }

    // Clean up timer on component unmount or modal state change
    return () => {
      if (navigationTimer) clearTimeout(navigationTimer);
    };
  }, [showSuccessModal, goToInbox]);

  return (
    <>
      {/* Hidden button container */}
      <Box
        className={`docbtn ${className}`}
        onClick={handleSplitClick}
        sx={{ display: "none" }}
      />

      {/* Split Modal */}
      <Dialog
        open={showSplitModal}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h6"
            fontWeight="bold"
            fontFamily="Manrope, sans-serif"
          >
            Split Document into Child Pages
          </Typography>
        </DialogTitle>

        <DialogContent>
          {errorMsg && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                mb: 2,
                fontFamily: "Manrope, sans-serif",
                fontSize: "14px",
              }}
            >
              {errorMsg}
            </Alert>
          )}

          <DialogContentText
            sx={{
              color: "black",
              fontFamily: "Manrope, sans-serif",
              mb: 2,
            }}
          >
            Type in the pages or page ranges you want to extract and create as
            separate documents.
          </DialogContentText>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Ex: 2, 3-5, 6"
            value={pageNumber}
            onChange={(e) => {
              setPageNumber(e.target.value);
              setErrorMsg("");
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00AB4E",
                },
              },
            }}
            size="small"
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={closeModal}
            disabled={isLoading}
            sx={{
              borderRadius: 1,
              borderColor: "#00AB4E",
              color: "#00AB4E",
              textTransform: "none",
              padding: "6px 16px",
              minWidth: "100px",
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSplit}
            disabled={isLoading}
            sx={{
              borderRadius: 1,
              backgroundColor: "#00AB4E",
              color: "white",
              textTransform: "none",
              padding: "6px 16px",
              minWidth: "100px",
              "&:hover": {
                backgroundColor: "#009045",
              },
            }}
          >
            {isLoading ? (
              <>
              </>
            ) : (
              "SPLIT DOCUMENT"
            )}
          </Button>
        </DialogActions>

        {isLoading && (
          <Box sx={{ p: 2 }}>
            <CommanSpinner spinner={isLoading} text="Splitting Documents" />
          </Box>
        )}
      </Dialog>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px", // Adjust this value to control roundness
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h6"
            fontWeight="bold"
            fontFamily="Manrope, sans-serif"
          >
            Split Complete
          </Typography>
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ fontSize: "14px" }}>
            The document has been successfully split. Redirecting to inbox...
          </DialogContentText>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            {/* <CircularProgress size={24} sx={{ color: "#00AB4E" }} /> */}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PDFSplitter;